"use client";

import * as React from "react";
import { CheckIcon, CircleIcon, MinusIcon, XIcon, type Icon } from "@phosphor-icons/react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";
import { InfoIcon, NoticeIcon } from "../images";

const badgeVariants = cva(
  "focus:ring-ring inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-hidden",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/80 border-transparent",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 border-transparent",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/80 border-transparent",
        outline: "text-foundation-foreground",
        brand: "border-[#f0bb31] bg-[#f0bb3133] hover:bg-[#f0bb3166]",
        success: "border-success bg-success/20 hover:bg-success/40",
        info: "border-info bg-info/20 hover:bg-info/40",
        warning: "border-warning bg-warning/20 hover:bg-warning/40",
        aqua: "border-aqua bg-aqua/20 hover:bg-aqua/40",
        midnight: "border-midnight bg-midnight/20 hover:bg-midnight/40",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

// ⬇️ ICON BADGE
const iconBadgeVariants = cva(
  "bg-badge-rest border-badge-rest-border hover:bg-badge-hovered hover:border-badge-hovered-border focus:ring-ring inline-flex max-w-full items-center justify-center rounded-lg border p-1 transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-hidden",
  {
    variants: {
      size: {
        xl: "min-h-14 min-w-14",
        lg: "min-h-12 min-w-12",
        sm: "min-h-8 min-w-8",
      },
    },
    defaultVariants: {
      size: "sm",
    },
  },
);

export interface IconBadgeProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof iconBadgeVariants> {
  icon?: ((props: React.SVGProps<SVGSVGElement>) => React.JSX.Element) | Icon | (() => React.JSX.Element);
  label?: string;
}

function IconBadge({ className, size, label, icon: Icon, ...props }: IconBadgeProps) {
  return (
    <div
      className={cn(
        iconBadgeVariants({ size }),
        {
          "group flex flex-col items-center justify-center px-0.5": label,
        },
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          "transition-transform ease-in-out",
          label ? "translate-y-1 pt-0.5 group-hover:translate-y-0" : "",
          size === "xl" ? "size-8" : size === "lg" ? "size-6" : "size-4",
        )}
      >
        {Icon && <Icon className="text-icon h-full w-full" />}
      </div>
      {label && (
        <span
          className={cn(
            "text-[8px] font-semibold opacity-0 transition delay-75 ease-in-out",
            "group-hover:translate-y-0.5 group-hover:opacity-100",
          )}
        >
          {label}
        </span>
      )}
    </div>
  );
}

// ⬇️ STATUS BADGE
const statusBadgeVariants = cva("flex size-6 items-center justify-center rounded-full p-0", {
  variants: {
    type: {
      positive: "bg-status-positive",
      negative: "bg-status-negative",
      inactive: "bg-status-inactive",
    },
  },
  defaultVariants: {
    type: "positive",
  },
});

export interface StatusBadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof statusBadgeVariants> {}

function StatusBadge({ className, type, ...props }: StatusBadgeProps) {
  const colors = {
    positive: "bg-status-positive-foreground",
    negative: "bg-status-negative-foreground",
    inactive: "bg-status-inactive-foreground",
  };
  return (
    <div className={cn("animate-pulse", statusBadgeVariants({ type }), className)} {...props}>
      <div className={cn("size-[14px] rounded-full", colors[type || "positive"])}></div>
    </div>
  );
}

// ⬇️ STICKER BADGE

const stickerBadgeVariants = cva("flex size-[20px] items-center justify-center rounded-full", {
  variants: {
    type: {
      success: "bg-sticker-success p-[3px]",
      failure: "bg-sticker-failure p-[3px]",
      notice: "bg-status-negative-foreground p-[3px]",
      information: "bg-sticker-information p-[3px]",
      active: "border-sticker-active border px-0 py-1",
      neutral: "bg-sticker-neutral p-[3px]",
      inactive: "border-bluish-gray-400 border p-[3px]",
    },
  },
  defaultVariants: {
    type: "neutral",
  },
});

export interface StickerBadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof stickerBadgeVariants> {}

function StickerBadge({ className, type, ...props }: StickerBadgeProps) {
  const renderIcon = () => {
    switch (type) {
      case "success":
        return <CheckIcon size={16} weight="bold" />;
      case "failure":
        return (
          <div className="flex size-4 items-center justify-center">
            <XIcon size={16} weight="bold" />
          </div>
        );
      case "notice":
        return (
          <div className="flex size-4 pl-[1px]">
            <NoticeIcon />
          </div>
        );
      case "information":
        return (
          <div className="flex size-4 items-center justify-center">
            <InfoIcon />
          </div>
        );
      case "active":
        return <CircleIcon size={16} color="#5184DC" weight="fill" />;
      case "neutral":
        return <MinusIcon size={16} weight="bold" />;
      case "inactive":
        return <div className="size-5 rounded bg-transparent"></div>;
      default:
        return <div className="border-sticker-neutral size-[20px] rounded-full border"> </div>;
    }
  };
  return (
    <div className={cn(stickerBadgeVariants({ type }), className)} {...props}>
      {renderIcon()}
    </div>
  );
}
export {
  Badge,
  StatusBadge,
  StickerBadge,
  IconBadge,
  badgeVariants,
  iconBadgeVariants,
  statusBadgeVariants,
  stickerBadgeVariants,
};
